import * as Sentry from "@sentry/react";

import { apiFetch } from "@/lib/api";

/**
 * Per-record verdict from the server's reconcile endpoint.
 *
 *  - `"acked"`   — the mutation already landed in a prior session; the record
 *                  is saved server-side. The outbox entry can be dropped
 *                  without re-uploading.
 *  - `"unknown"` — the server has no record of this `client_uuid`; the drain
 *                  must upload it normally.
 */
export type ReconcileStatus = "acked" | "unknown";

/**
 * Shape of `GET /core/1.0/mutations/reconcile`: a flat map of every queried
 * `client_uuid` to its {@link ReconcileStatus}.
 */
type ReconcileResponse = Record<string, ReconcileStatus>;

/**
 * Backend cap — at most 256 `client_uuids` may be queried per request.
 * Larger outboxes are chunked into multiple batches.
 */
const MAX_UUIDS_PER_REQUEST = 256;

/** Split `uuids` into contiguous batches of at most {@link MAX_UUIDS_PER_REQUEST}. */
function chunk(uuids: string[]): string[][] {
  const batches: string[][] = [];
  for (let i = 0; i < uuids.length; i += MAX_UUIDS_PER_REQUEST) {
    batches.push(uuids.slice(i, i + MAX_UUIDS_PER_REQUEST));
  }
  return batches;
}

/**
 * Asks the server which queued records already landed in a prior session.
 *
 * Before the outbox drain re-uploads anything on boot, this calls
 * `GET /core/1.0/mutations/reconcile?client_uuids=<comma-separated>` — chunked
 * into batches of ≤256 — and merges every batch's verdict into one map.
 *
 * Reconcile is purely a **bandwidth optimisation**: an `"acked"` record can
 * skip a wasteful multi-megabyte re-upload. It is NOT a correctness
 * dependency — the upload's `X-Idempotency-Key` is what guarantees no
 * duplicates. So a failed reconcile request degrades gracefully: every
 * `client_uuid` in that failed batch is mapped to `"unknown"`, meaning the
 * drain simply uploads it normally (idempotency keeps that safe).
 *
 * @param clientUuids  the `clientUuid` of every pending outbox entry.
 * @param getAccessToken  the WorkOS access-token getter, threaded to `apiFetch`.
 * @returns a map of every input `clientUuid` to its reconcile status. Every
 *   input uuid is always present in the result.
 */
export async function reconcilePendingRecords(
  clientUuids: string[],
  getAccessToken: () => Promise<string>,
): Promise<Map<string, ReconcileStatus>> {
  const result = new Map<string, ReconcileStatus>();
  if (clientUuids.length === 0) return result;

  const batches = chunk(clientUuids);

  // Batches are independent — query them concurrently. Each batch settles
  // on its own: one failing batch degrades only its own uuids to `unknown`.
  await Promise.all(
    batches.map(async (batch) => {
      try {
        const response = await apiFetch<ReconcileResponse>(
          `/core/1.0/mutations/reconcile?client_uuids=${batch.join(",")}`,
          getAccessToken,
        );
        for (const uuid of batch) {
          // Trust the server's verdict; anything it omits or returns
          // unexpectedly is treated as `unknown` so the drain re-uploads.
          result.set(uuid, response[uuid] === "acked" ? "acked" : "unknown");
        }
      } catch (err) {
        // Reconcile is an optimisation, not correctness — a failed request
        // degrades every uuid in this batch to `unknown`. The drain will
        // upload them; idempotency makes a redundant re-upload harmless.
        // The degrade is intentional, but it is still a failure worth
        // surfacing: a chronically broken reconcile endpoint means every
        // boot re-uploads megabytes needlessly. Report it (warning — not
        // correctness-critical) rather than log-and-swallow. See
        // [[error-reporting]] "Offline and outbox errors".
        Sentry.captureException(err, {
          level: "warning",
          tags: { area: "outbox", phase: "reconcile-batch" },
        });
        for (const uuid of batch) {
          result.set(uuid, "unknown");
        }
      }
    }),
  );

  return result;
}
